import { IonToast, useIonActionSheet } from '@ionic/react';
import { useCallback, useState } from 'react';

import { useHistory } from 'react-router';
import { useFeedFiltersContext } from '../hooks/useFeedFilters';
import { useLoggedIn } from '../hooks/useLoggedIn';
import { useTopicPreferences } from '../hooks/useTopicPreferences';
// eslint-disable-next-line css-modules/no-unused-class
import shareStyles from '../styles/Shared.module.css';
import styles from './TopicButton.module.css';

export function useManageTopicsSheet(
	following: boolean,
	hidden: boolean
): (onDidDismiss: (event: CustomEvent) => void) => void {
	const [presentTopicOptions] = useIonActionSheet();
	return useCallback(
		(onDidDismiss: (event: CustomEvent) => void) => {
			const buttons = [
				{
					text: following ? 'Unsubscribe from this topic' : 'Subscribe to this topic',
					data: {
						action: following ? 'unsubscribe' : 'subscribe'
					}
				},
				{
					text: hidden ? 'Unhide this topic' : 'Hide this topic',
					data: {
						action: hidden ? 'unhide' : 'hide'
					}
				},
				{
					text: 'Manage my topics',
					data: {
						action: 'manage'
					}
				},
				{
					text: 'Filter to this topic',
					data: {
						action: 'filter'
					}
				},
				{
					text: 'Cancel',
					role: 'cancel',
					data: {
						action: 'cancel'
					}
				}
			];
			presentTopicOptions({
				header: 'Manage Topics',
				buttons,
				onDidDismiss
			});
		},
		[presentTopicOptions, following, hidden]
	);
}
export interface TopicButtonProps {
	topic: string;
	testId?: string;
}
export function TopicButton({ topic, testId }: TopicButtonProps): JSX.Element {
	const history = useHistory();
	const { setFilters } = useFeedFiltersContext();
	const { topicPreferences, updateTopicPreferences, isError, isLoading, isSuccess } = useTopicPreferences();
	const [toast, setToast] = useState<string | undefined>();
	const loggedIn = useLoggedIn();
	const onDismissTopicsManageSheet = async ({ detail }: CustomEvent) => {
		if (!loggedIn.realUser) {
			if (detail.data.action === 'filter') {
				setFilters({
					topic
				});
			} else {
				setToast('Please log in to manage topics');
			}
			return;
		}
		if (topicPreferences === undefined || topic === undefined) {
			return;
		}

		switch (detail.data.action) {
			case 'unhide':
				await updateTopicPreferences({
					...topicPreferences,
					hidden: topicPreferences.hidden.filter(hiddenTopic => hiddenTopic !== topic)
				});
				setToast(`Unhidden topic ${topic}`);
				break;
			case 'hide':
				await updateTopicPreferences({
					...topicPreferences,
					hidden: [...topicPreferences.hidden, topic],
					following: topicPreferences.following.filter(followedTopic => followedTopic !== topic)
				});
				setToast(`Hidden topic ${topic}`);
				break;
			case 'unsubscribe':
				await updateTopicPreferences({
					...topicPreferences,
					following: topicPreferences.following.filter(followedTopic => followedTopic !== topic)
				});
				setToast(`Unsubscribed from topic ${topic}`);
				break;
			case 'subscribe':
				await updateTopicPreferences({
					...topicPreferences,
					following: [...topicPreferences.following, topic],
					hidden: topicPreferences.hidden.filter(hiddenTopic => hiddenTopic !== topic)
				});
				setToast(`Subscribed to topic ${topic}`);
				break;
			case 'manage':
				setToast(undefined);
				history.push('/main/settings/topics');
				break;
			case 'filter':
				setFilters({
					topic
				});
				break;
		}
	};
	const showManageTopicsSheet = useManageTopicsSheet(
		topicPreferences?.following.includes(topic ?? '') ?? false,
		topicPreferences?.hidden.includes(topic ?? '') ?? false
	);
	return (
		<>
			<span
				className={[
					shareStyles.pill,
					topicPreferences?.following?.includes(topic) ? styles.followedTopic : '',
					topicPreferences?.hidden?.includes(topic) ? styles.hiddenTopic : ''
				].join(' ')}
				onClick={(evt: React.SyntheticEvent<HTMLSpanElement, MouseEvent>) => {
					if (isLoading) {
						return;
					}
					showManageTopicsSheet(onDismissTopicsManageSheet);
				}}
				data-testid={testId}
			>
				{topic}
			</span>
			<IonToast
				isOpen={toast !== undefined && (isError || isSuccess)}
				duration={1500}
				message={toast}
				position="top"
				onDidDismiss={() => setToast(undefined)}
			/>
		</>
	);
}
